import React from 'react';
import UiIcon from '../ui/UiIcon';

// Building blocks shared by the Profit and Engagement tabs (styles in styles/analytics.css)

export const INK = { secondary: '#52514e', muted: '#898781', grid: '#e1e0d9', axis: '#c3c2b7' };
export const SURFACE = '#fcfcfb';

export const numFormat =new Intl.NumberFormat('en-GB');
export const pct = (v) => (v == null ? '–' : `${Math.round(v * 100)}%`);
export const shortDate = (key) => new Date(`${key}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
export const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });

export function addDays(key, n) {
  const d = new Date(`${key}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export const axisProps = {
  tick: { fill: INK.muted, fontSize: 12 },
  tickLine: false,
  axisLine: { stroke: INK.axis },
};

export function ChartTooltip({ active, payload, label, labelFormatter, valueFormatter, filter }) {
  if (!active || !payload?.length) return null;
  const rows = filter ? payload.filter(p => filter(p, payload)) : payload;
  return (
    <div className="pf-tooltip">
      <div className="pf-tooltip-label">{labelFormatter ? labelFormatter(label) : label}</div>
      {rows.map(p => (
        <div key={p.dataKey} className="pf-tooltip-row">
          <span className="pf-swatch" style={{ background: p.color }} />
          <span className="pf-tooltip-name">{p.name}</span>
          <span className="pf-tooltip-value">{valueFormatter ? valueFormatter(p.value, p) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

// Change against the previous period. goodWhenUp: true / false colors it, null leaves it neutral
export function Delta({ value, previous, goodWhenUp = true, period = '30 days', short = false }) {
  if (previous == null || value == null || previous === 0) return null;
  const change = (value - previous) / Math.abs(previous);
  const up = change > 0;
  const good = goodWhenUp === null ? null : (up === goodWhenUp);
  return (
    <span className={`pf-delta ${good === null ? '' : good ? 'good' : 'bad'}`} title={`vs prev. ${period}`}>
      {up ? '▲' : '▼'} {Math.abs(Math.round(change * 100))}%{short ? '' : ` vs prev. ${period}`}
    </span>
  );
}

// One line of a compact metric list: label (and a muted note) left, value and change right
export function MetricRow({ label, note, value, previous, goodWhenUp = true, format, period }) {
  return (
    <div className="pf-metric">
      <span className="pf-metric-label">{label}{note && <span className="pf-metric-note">{note}</span>}</span>
      <span className="pf-metric-value">
        {format ? format(value) : value}
        <Delta value={value} previous={previous} goodWhenUp={goodWhenUp} period={period} short />
      </span>
    </div>
  );
}

// goodWhenUp: true / false colors the change, null leaves it neutral
export function StatTile({ label, value, previous, goodWhenUp = true, format, hero = false, period = '30 days', note }) {
  const delta = <Delta value={value} previous={previous} goodWhenUp={goodWhenUp} period={period} />;
  return (
    <div className={`pf-tile ${hero ? 'hero' : ''}`}>
      <div className="pf-tile-label">{label}</div>
      <div className="pf-tile-value">{format ? format(value) : value}</div>
      {delta}
      {note && <span className="pf-delta">{note}</span>}
    </div>
  );
}

export function Section({ title, hint, children, actions, className = '' }) {
  return (
    <section className={`pf-section ${className}`}>
      <div className="pf-section-head">
        <div>
          <h2>{title}</h2>
          {hint && <p className="pf-hint">{hint}</p>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function TableToggle({ open, onToggle }) {
  return (
    <button className="pf-link-button" onClick={onToggle}>{open ? 'Hide table' : 'Show table'}</button>
  );
}

// Segmented control for picking a time range. options: [{ value, label }]
export function RangePicker({ options, value, onChange }) {
  return (
    <div className="pf-range">
      {options.map(o => (
        <button key={o.value} className={`pf-range-btn ${value === o.value ? 'active' : ''}`} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Downloads the tab's data for handing over for analysis: a readable summary (Markdown, with a
// guide for an LLM) and the full JSON
export function ExportButton({ data, name, buildReport }) {
  if (!data) return null;
  function download(content, type, extension) {
    const url = URL.createObjectURL(new Blob([content], { type }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `norppa-${name}-${new Date().toISOString().slice(0, 10)}.${extension}`;
    a.click();
    URL.revokeObjectURL(url);
  }
  return (
    <>
      <button className="btn-secondary" onClick={() => download(buildReport(), 'text/markdown', 'md')} title="Readable summary of this tab, with notes for an LLM">
        <UiIcon name="download" /> Export summary
      </button>
      <button className="btn-secondary" onClick={() => download(JSON.stringify(data), 'application/json', 'json')} title="All the raw data behind this tab">
        <UiIcon name="data_object" /> Full JSON
      </button>
    </>
  );
}
